import * as React from "react";
import { cn } from "@/lib/utils";

const Table = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & { ref?: React.RefObject<HTMLTableElement | null> }) => (
  <div className="table-scroll-container relative w-full overflow-auto">
    <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
  </div>
);
Table.displayName = "Table";

const TableHeader = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.RefObject<HTMLTableSectionElement | null>;
}) => (
  // bg-muted (not bg-muted/50) is deliberate — see the note on TableRow's
  // bg-card below. A translucent header background would blend with
  // whatever's scrolled underneath the sticky first header cell instead of
  // hiding it.
  <thead ref={ref} className={cn("[&_tr]:border-b [&_tr]:bg-muted", className)} {...props} />
);
TableHeader.displayName = "TableHeader";

const TableBody = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.RefObject<HTMLTableSectionElement | null>;
}) => <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
TableBody.displayName = "TableBody";

const TableFooter = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.RefObject<HTMLTableSectionElement | null>;
}) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props}
  />
);
TableFooter.displayName = "TableFooter";

const TableRow = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  ref?: React.RefObject<HTMLTableRowElement | null>;
}) => (
  <tr
    ref={ref}
    className={cn(
      // bg-card (not bg-inherit) is deliberate: `inherit` only copies the
      // *immediate* parent's computed background-color, one hop — <tbody>,
      // <table>, and table-scroll-container's own wrapper div never set
      // their own background, so `inherit` resolves to transparent there,
      // and a transparent row means its sticky first cell (see
      // table-scroll-container in globals.css, which also relies on
      // `background-color: inherit`, one hop from this row) shows whatever
      // scrolled-under cell is visually behind it instead of hiding it.
      // bg-card gives every row a real, opaque color to hand down instead —
      // it matches the Card most tables render inside; the couple of tables
      // that sit directly on the page background render a very slightly
      // lighter row area, which reads as an intentional surface rather than
      // a rendering bug.
      //
      // hover:bg-muted (not bg-muted/50) is opaque for the same reason: the
      // sticky first cell's `background-color: inherit` (see
      // table-scroll-container in globals.css) repaints the row's hover
      // color a second time, on top of the row's own copy of it. Two
      // stacked layers of the *same* translucent color composite into a
      // visibly darker/more saturated result than the single layer showing
      // through the row's other (background-less) cells — the sticky
      // column would read as a different color on hover. Opaque colors
      // don't have this problem: painting the same solid color twice looks
      // identical to painting it once.
      "border-b bg-card transition-colors hover:bg-muted data-[state=selected]:bg-muted",
      className,
    )}
    {...props}
  />
);
TableRow.displayName = "TableRow";

const TableHead = ({
  ref,
  className,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.RefObject<HTMLTableCellElement | null>;
}) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const TableCell = ({
  ref,
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.RefObject<HTMLTableCellElement | null>;
}) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
);
TableCell.displayName = "TableCell";

const TableCaption = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement> & {
  ref?: React.RefObject<HTMLTableCaptionElement | null>;
}) => (
  <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
